import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslate } from '@tolgee/react';
import { AppRoute } from '@agentselly/immodossier-shared';
import { useLoaderQuery } from '~/helper/useLoaderQuery';
import { useAppParams } from '~/helper/useAppParams';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { RenderWidgets } from '~/components/widget/RenderWidgets';
import { cockpitStatisticsWidgets } from '~/config/widgets/cockpit/cockpitStatisticsWidgets';
import { AppLink } from '~/components/AppLink';
import {
  fmtDate,
  Kpi,
  LineChart,
  PageHead,
  V3Page,
} from '~/components/cockpit/v3';
import {
  filterWeeklyBuckets,
  weekStartDateString,
} from '~/util/statisticsChart';
import type { WidgetProps } from '~/components/cockpit/cockpitData';

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_STATISTICS,
)({
  component: StatisticsPage,
});

const RANGES = [
  '7d',
  '30d',
  'all',
] as const;

function StatisticsPage(): React.ReactElement {
  const params = useAppParams(Route);
  const { t } = useTranslate();
  const [
    metric,
    setMetric,
  ] = useState(0);
  const [
    range,
    setRange,
  ] = useState<(typeof RANGES)[number]>('all');

  const query = useLoaderQuery(
    AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_STATISTICS,
    { params },
  );

  if (query.isLoading) return <LoadingWidget />;
  if (query.isError || !query.data) return <ErrorWidget error={query.error} />;

  if (!query.data.v3Redesign) {
    const widgetProps = query.data?.widgetProps || {};
    const widgets = cockpitStatisticsWidgets(widgetProps);
    return (
      <div className="h-full flex flex-col gap-y-6">
        <RenderWidgets widgets={widgets} />
      </div>
    );
  }

  const wp = (query.data.widgetProps || {}) as WidgetProps;
  const metrics = [
    {
      key: 'cockpitStatisticsAdViews',
      label: t('cockpit_stats_views', 'Inserat-Aufrufe'),
    },
    {
      key: 'cockpitStatisticsDigitalTourViews',
      label: t('cockpit_stats_tour', 'Digitale Tour'),
    },
    {
      key: 'cockpitStatisticsExposeDownloadsViews',
      label: t('cockpit_stats_expose', 'Exposé-Downloads'),
    },
  ];
  const cur = metrics[metric];
  const d = wp[cur.key] ?? {};
  const buckets = filterWeeklyBuckets(d.lineChartData ?? [], range);
  const links: { label: string; url: string }[] = d.links ?? [];

  return (
    <V3Page>
      <PageHead
        title={t('cockpit_stats_title', 'Statistik')}
        sub={t(
          'cockpit_stats_sub',
          'So entwickelt sich das Interesse an Ihrer Immobilie.',
        )}
      />
      <div
        className="tab"
        role="group"
        aria-label={t('cockpit_stats_title', 'Statistik')}
      >
        {metrics.map((m, i) => (
          <button
            key={m.key}
            type="button"
            className={i === metric ? 'on' : ''}
            aria-pressed={i === metric}
            onClick={() => setMetric(i)}
          >
            {m.label}
          </button>
        ))}
      </div>
      <div className="cols">
        <section
          className="card"
          data-widget={cur.key}
        >
          <div className="sec">
            <h2>{cur.label}</h2>
            <div className="fch">
              {RANGES.map((r) => (
                <button
                  key={r}
                  type="button"
                  aria-pressed={r === range}
                  onClick={() => setRange(r)}
                >
                  {r === '7d'
                    ? t('cockpit_stats_7d', '7 Tage')
                    : r === '30d'
                    ? t('cockpit_stats_30d', '30 Tage')
                    : t('cockpit_stats_all', 'Gesamt')}
                </button>
              ))}
            </div>
          </div>
          {buckets.length ? (
            <LineChart
              title={cur.label}
              values={buckets.map((b) => b.count)}
              labels={[
                fmtDate(weekStartDateString(buckets[0].week)),
                fmtDate(weekStartDateString(buckets[buckets.length - 1].week)),
              ]}
            />
          ) : (
            <p className="mu">
              {t('cockpit_stats_empty', 'Noch keine Daten vorhanden.')}
            </p>
          )}
        </section>
        <aside className="side">
          <div className="grid g2">
            <Kpi
              value={d.value ?? '–'}
              delta={d.changeInLastWeek}
              label={t('cockpit_stats_total', 'Total')}
            />
            <Kpi
              value={buckets.reduce((s, b) => s + b.count, 0)}
              label={t('cockpit_stats_range', 'Im Zeitraum')}
            />
          </div>
          <section className="card">
            <dl className="dl">
              <div>
                <dt>{t('cockpit_stats_online', 'Online seit')}</dt>
                <dd>{fmtDate(d.onlineSinceDate)}</dd>
              </div>
              <div>
                <dt>{t('cockpit_stats_changed', 'Letzte Änderung')}</dt>
                <dd>{fmtDate(d.lastChangeDate)}</dd>
              </div>
            </dl>
          </section>
          {links.length || d.interestLink ? (
            <section className="card">
              <div className="sec">
                <h2>{t('cockpit_stats_links', 'Links')}</h2>
              </div>
              {links.map((l) => (
                <p key={l.url}>
                  <a
                    className="lnk"
                    href={l.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {l.label}
                  </a>
                </p>
              ))}
              {d.interestLink ? (
                <AppLink
                  to={
                    AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_INTEREST
                  }
                  params={params}
                  className="lnk"
                >
                  {t('cockpit_stats_to_interest', 'Zu den Interessenten')}
                </AppLink>
              ) : null}
            </section>
          ) : null}
        </aside>
      </div>
    </V3Page>
  );
}
